import { next } from '@ember/runloop';
import { isNone } from '@ember/utils';

import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type BoardRecord from 'webapp/models/board';
import { DEFAULT_BG_IMAGE_OPACITY } from 'webapp/models/board';

export default class BoardSerializer extends ApplicationSerializer {
	override getReadOnlyFields(_json: Record<string, unknown>): RecordData.Board.ReadOnlyKey[] {
		return [
			'created',
			'createdById',
			'modified',
			'modifiedById',
			'lastSync',
			'sources',
			'originateFrom',
		];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Board.Raw | RecordData.Board.Serialized,
		prop: string | undefined,
	) {
		const { settings, ...data } = json;
		const attributes: Partial<RecordAttributes<BoardRecord>> & { id?: string | number } = {
			...data,
		};

		if ('createdBy' in data && data.createdBy) {
			attributes.createdById = data.createdBy.id;
		}
		if ('modifiedBy' in data && data.modifiedBy) {
			attributes.modifiedById = data.modifiedBy.id;
		}

		if (settings) {
			if (typeof settings.view !== 'undefined') {
				attributes.primaryView = settings.view;
			}
			if (typeof settings.logo !== 'undefined') {
				attributes.logo = settings.logo;
			}
			if (typeof settings.logoOriginal !== 'undefined') {
				attributes.logoOriginal = settings.logoOriginal;
			}
			if (typeof settings.logoCrop !== 'undefined') {
				attributes.logoCrop = settings.logoCrop;
			}
			if (typeof settings.bg !== 'undefined') {
				attributes.bg = settings.bg;
			}
			if (typeof settings.bgOriginal !== 'undefined') {
				attributes.bgOriginal = settings.bgOriginal;
			}
			if (typeof settings.bgCrop !== 'undefined') {
				attributes.bgCrop = settings.bgCrop;
			}
			if (typeof settings.bgOpacity !== 'undefined') {
				attributes.bgOpacity = settings.bgOpacity;
			}
			if (typeof settings.restrictIps !== 'undefined') {
				attributes.restrictIps = settings.restrictIps;
			}
			if (typeof settings.password !== 'undefined') {
				attributes.password = settings.password;
			}
			attributes.tags = settings.tags || [];
		}

		if (!attributes.bgOpacity) {
			attributes.bgOpacity = DEFAULT_BG_IMAGE_OPACITY;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Board.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<BoardRecord>,
			RecordData.Board.ReadOnlyKey
		>;

		const settings: RecordData.Board.Settings = {};
		if (attributes.logo) {
			settings.logo = attributes.logo;
			if (attributes.logoOriginal) {
				settings.logoOriginal = attributes.logoOriginal;
			}
			if (attributes.logoCrop) {
				settings.logoCrop = attributes.logoCrop;
			}
		}
		if (attributes.bg) {
			settings.bg = attributes.bg;
			if (attributes.bgOriginal) {
				settings.bgOriginal = attributes.bgOriginal;
			}
			if (attributes.bgCrop) {
				settings.bgCrop = attributes.bgCrop;
			}
			if (attributes.bgOpacity) {
				settings.bgOpacity = attributes.bgOpacity;
			}
		}
		if (attributes.restrictIps) {
			settings.restrictIps = attributes.restrictIps;
		}
		if (attributes.password) {
			settings.password = attributes.password;
		}
		// BE clears settings that are not sent, so the mobile view (0) has to be sent too
		if (!isNone(attributes.primaryView)) {
			settings.view = attributes.primaryView;
		}
		if (attributes.tags) {
			settings.tags = attributes.tags;
		}

		const json: RecordData.Board.Serialized = {
			...omit(
				attributes,
				// moved to settings
				'logo',
				'logoOriginal',
				'logoCrop',
				'bg',
				'bgOriginal',
				'bgCrop',
				'bgOpacity',
				'restrictIps',
				'password',
				'tags',
				'primaryView',
				// not sent
				'newBadge',
				'verificationInfo',
				'isSample',
			),
			settings,
		};

		// isSample is a transient flag - sent to BE once, then reset
		// Uses next() to defer the reset so it doesn't run before the actual save
		// (request-queue calls model.serialize() for diff comparison before model.save())
		if (attributes.isSample) {
			json.isSample = true;
			next(() => {
				(snapshot.record as BoardRecord).isSample = false;
			});
		}

		return json;
	}
}
